<template>
  <custom-collapse-item
    ref="collapseItem"
    auto
    class="my-3 collapse-item section bg-gray"
    expanded-icon="fas fa-chevron-circle-right color-gray-7"
    minimized-icon="fas fa-chevron-circle-down color-warning"
    parent-id="svp-collapse"
    read-only-icon="fas fa-plus-circle color-gray-2"
    start-expanded
    title="SVP - Nivel de Preparación Vocacional"
    @expandButtonClick="$emit('expandButtonClick')"
  >
    <div class="bg-white p-3 rounded">
      <div class="bg-light p-3 rounded mb-3 border">
        <div class="bg-white svp-legend-box">
          <div
            v-for="(group, index) in legendGroups"
            :key="group?.name"
            class="row align-items-center mb-3"
            :class="index < 2 ? 'border-bottom pb-3' : ''"
          >
            <div class="col-md-2 font-weight-bold">
              {{ group?.name }}
            </div>

            <div class="col-md-10 d-flex flex-wrap">
              <span
                v-for="legend in group.legends"
                :key="legend.id"
                class="svp-chip mr-3 mb-2"
                :style="getLegendStyle(legend)"
              >
                {{ legend?.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="svp-item-card p-3">
        <div class="row">
          <div class="col-md-6 mb-3">
            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors }"
              name="Tipo de Trabajo Requerido por la Profesión"
              rules="required"
            >
              <label>Tipo de Trabajo Requerido por la Profesión:</label>
              <custom-drop-down
                clearable
                :disabled="isReadOnly"
                fieldkey="id"
                fieldname="name"
                :options="workTypes"
                placeholder="Seleccione"
                :value="item.workType"
                @input="onWorkTypeChange"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
            <div v-else>
              <label>Tipo de Trabajo Requerido por la Profesión:</label>
              <span class="d-block mt-1 text-muted">
                {{ item.workType?.name | empty }}
              </span>
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors }"
              name="Nivel Requerido de la Profesión"
              rules="required"
            >
              <label>Nivel Requerido de la Profesión:</label>
              <custom-drop-down
                clearable
                :disabled="isReadOnly || !item.workType"
                fieldkey="id"
                fieldname="name"
                :options="filteredLevels"
                placeholder="Seleccione"
                :value="item.requiredLevel"
                @input="updateItem('requiredLevel', $event)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
            <div v-else>
              <label>Nivel Requerido de la Profesión:</label>
              <span class="d-block mt-1 text-muted">
                {{ item.requiredLevel?.name | empty }}
              </span>
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors }"
              name="Perfil del Evaluado"
              rules="required"
            >
              <label>Perfil del Evaluado:</label>
              <custom-drop-down
                clearable
                :disabled="isReadOnly"
                fieldkey="id"
                fieldname="name"
                :options="filteredNotParentLevels"
                placeholder="Seleccione"
                :value="item.evaluatedLevel"
                @input="updateItem('evaluatedLevel', $event)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
            <div v-else>
              <label>Perfil del Evaluado:</label>
              <span class="d-block mt-1 text-muted">
                {{ item.evaluatedLevel?.name | empty }}
              </span>
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <label>Nivel de Alcance:</label>
            <div>
              <span
                v-if="getLegendByLevel(item.evaluatedLevel)"
                class="svp-chip"
                :style="getLegendStyle(getLegendByLevel(item.evaluatedLevel))"
              >
                {{ getLegendByLevel(item.evaluatedLevel)?.name }}
              </span>
            </div>
          </div>
        </div>

        <hr />

        <validation-provider
          v-slot="{ errors }"
          name="Resultado del Pareo"
          rules="required"
        >
          <input type="hidden" :value="item.selectedAnswer" />

          <div class="d-flex flex-wrap svp-answer-wrapper">
            <button
              v-for="answer in answers"
              :key="`svp-answer-${answer.id}`"
              class="svp-answer-btn"
              :class="{
                active:
                  item.selectedAnswer && item.selectedAnswer.id === answer.id,
              }"
              :disabled="isReadOnly"
              type="button"
              @click="updateItem('selectedAnswer', answer)"
            >
              <i class="far fa-check-circle d-block mb-1" />
              {{ answer?.name }}
            </button>
          </div>

          <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
        </validation-provider>
      </div>

      <div class="mt-3">
        <label>Observaciones:</label>
        <custom-text-area
          v-model="model.comment"
          :count-classes="['d-block', 'small-counter', 'text-right']"
          :disabled="isReadOnly"
          :maxlength="5000"
          placeholder="Comentario"
          show-char-count
        />
      </div>
    </div>
  </custom-collapse-item>
</template>

<script>
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';

export default {
  name: 'SvpOccupationalFactors',
  components: {
    CustomCollapseItem,
    CustomDropDown,
    CustomTextArea,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    item() {
      return this.model.item || {};
    },
    workTypes() {
      return this.model.workTypes || [];
    },
    legends() {
      return this.model.legends || [];
    },
    answers() {
      return this.model.answers || [];
    },
    legendGroups() {
      return [
        {
          name: 'Trabajo no diestro',
          legends: this.legends.filter((x) => x.min >= 1 && x.max <= 2),
        },
        {
          name: 'Trabajo semi-diestro',
          legends: this.legends.filter((x) => x.min >= 3 && x.max <= 6),
        },
        {
          name: 'Trabajo diestro',
          legends: this.legends.filter((x) => x.min >= 7 && x.max <= 9),
        },
      ];
    },
    levels() {
      return this.model.levels || [];
    },
    filteredLevels() {
      if (!this.item.workType) return [];

      return this.levels.filter((x) => x.parentId === this.item.workType.id);
    },
    filteredNotParentLevels() {
      return this.levels.filter((x) => x.parentId !== null);
    },
  },
  methods: {
    setExpanded(expand) {
      this.$refs.collapseItem.isExpended = expand;
    },
    updateItem(field, value) {
      this.model = {
        ...this.model,
        item: {
          ...this.item,
          [field]: value,
        },
      };
    },
    onWorkTypeChange(value) {
      this.model = {
        ...this.model,
        item: {
          ...this.item,
          workType: value,
          requiredLevel: null,
          evaluatedLevel: null,
        },
      };
    },
    getLegendStyle(legend) {
      if (!legend) return {};

      return {
        backgroundColor: `${legend.color}25`,
        border: `1px solid ${legend.color}`,
        color: legend.color,
      };
    },
    getLegendByLevel(level) {
      if (!level) return null;

      const levelId = typeof level === 'object' ? level.id : level;

      const levelIndex = this.levels.findIndex((x) => x.id === levelId);

      if (levelIndex === -1) return null;

      return this.legends[levelIndex] || null;
    },
  },
};
</script>

<style scoped>
.svp-legend-box {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1rem;
}

.svp-chip {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.svp-item-card {
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  background: #fafafa;
}

.svp-answer-wrapper {
  gap: 0.5rem;
}

.svp-answer-btn {
  flex: 1;
  border: 1px solid #f2f2f2;
  background: #f7f7f7;
  color: #a8b7b5;
  padding: 0.65rem 0.75rem;
  border-radius: 3px;
  font-size: 12px;
}

.svp-answer-btn i {
  font-size: 16px;
  color: #9bb0ad;
}

.svp-answer-btn.active {
  background: #2db782;
  border-color: #2db782;
  color: #fff;
}

.svp-answer-btn.active i {
  color: #fff;
}

.svp-answer-btn:disabled.active {
  background: #3f5b60;
  border-color: #3f5b60;
  color: #fff;
}

.svp-answer-btn:disabled:not(.active) {
  background: #f4f4f4;
  border-color: #f4f4f4;
  color: #aeb8b6;
  opacity: 1;
}
.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f7f7f7;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.collapse-item ::v-deep .color-warning {
  color: #ff5b5c !important;
}
::v-deep
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
</style>
